import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Snackbar, Alert } from '@mui/material';
import { COLORS_ADMIN_PAGE, ResizeHandle } from '../../conf/constants';
import { User, UserProfileProps, AccountFlags, Address } from '../types/type';
import { validateEmail, validatePhoneNumber } from '../../utils/validators';
import { useAddressManagement } from '../hooks/useAddressManagement';
import { useCityManagement } from '../hooks/useCityManagement';
import { useResizeHandler } from '../hooks/useResizeHandler';
import { useMapHandler } from '../hooks/useMapHandler';
import { UserBasicInfo } from './UserBasicInfo';
import { AddressList } from './AddressList';
import { AddressForm } from './AddressForm';
import { NewAddressDialog } from './NewAddressDialog';
import { AccountFlagsComponent } from './AccountFlags';
import MapPicker from './MapPicker';
import dayjs from 'dayjs';
import dataProvider from '../../services/api';

const UserProfile: React.FC<UserProfileProps> = ({
  selectedUser,
  setSelectedUser,
  handleOpenChangePasswordModal,
  handleSaveChanges,
  handleFileChange,
  onAddressSaveSuccess,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [openNewAddressDialog, setOpenNewAddressDialog] = useState(false);
  const [accountFlags, setAccountFlags] = useState<AccountFlags>({
    isAccountLocked: false,
    isEmailVerified: false,
    isClientPostAllowed: false,
    isContractorVerified: false,
    isSpecialContractor: false,
  });

  const {
    addresses,
    selectedAddress,
    setSelectedAddress,
    newAddress,
    setNewAddress,
    isGeocodingLoading,
    setIsGeocodingLoading,
    updateCoordinates,
    deleteAddress,
    saveAddress,
    saveNewAddress,
    resetNewAddress,
    snackbar, 
    handleSnackbarClose, 
    handleErrorWithLogout, 
    showSuccessMessage, 
    showErrorMessage,
  } = useAddressManagement(selectedUser);

  const {
    cityOptions,
    fetchInitialCities,
    handleCitySearch,
  } = useCityManagement();

  const {
    addressesWidth,
    isResizingAddresses,
    addressesContainerRef,
    handleAddressesMouseDown,
  } = useResizeHandler();

  const {
    openMapPicker,
    currentEditingCoordinates,
    handleOpenMapPicker,
    handleMapLocationSelect,
    handleCloseMapPicker,
  } = useMapHandler({
    selectedAddress,
    setSelectedAddress,
    newAddress,
    setNewAddress,
    setIsGeocodingLoading,
    showSuccessMessage,
    showErrorMessage,
    handleErrorWithLogout,
  });

  useEffect(() => {
    if (selectedUser) {
      setAccountFlags({
        isAccountLocked: selectedUser.isAccountLocked || false,
        isEmailVerified: selectedUser.isEmailVerified || false,
        isClientPostAllowed: selectedUser.isClientPostAllowed || false,
        isContractorVerified: selectedUser.isContractorVerified || false,
        isSpecialContractor: selectedUser.isSpecialContractor || false,
      });
    }
  }, [selectedUser]);

  useEffect(() => {
    if (openNewAddressDialog) {
      fetchInitialCities();
    }
  }, [openNewAddressDialog]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handlePhoneChange = (value: string) => {
    if (selectedUser) {
      setSelectedUser({ ...selectedUser, phoneNumber: value });
      const { error } = validatePhoneNumber(value);
      setPhoneError(error);
    }
  };

  const handlePrimaryChange = (checked: boolean) => {
    setSelectedAddress((prev:any) => ({
      ...prev,
      isPrimary: checked,
    }));
  };

  const handleEmailChange = (value: string) => {
    if (selectedUser) {
      setSelectedUser({ ...selectedUser, email: value });
      const { error } = validateEmail(value);
      setEmailError(error);
    }
  };

  const handleInputChange = (field: keyof User, value: string) => {
    if (selectedUser) {
      setSelectedUser({ ...selectedUser, [field]: value });
      if (field === 'email') {
        const { error } = validateEmail(value);
        setEmailError(error);
      }
    }
  };

  const handleAddressInputChange = (field: keyof Address, value: string) => {
    if (selectedAddress) {
      if (field === 'province') {
        setSelectedAddress({
          ...selectedAddress,
          province: { ...selectedAddress.province, name: value },
        });
      } else {
        setSelectedAddress({ ...selectedAddress, [field]: value });
      }
      
      const addressString = [
        selectedAddress.unit,
        selectedAddress.building,
        selectedAddress.street,
        selectedAddress.subdivision,
        selectedAddress.city?.name,
        selectedAddress.province.name,
        selectedAddress.zipCode,
      ]
        .filter(Boolean)
        .join(', ');
      updateCoordinates(addressString, false);
    }
  };

  const handleNewAddressInputChange = (field: keyof typeof newAddress, value: string | number) => {
    setNewAddress(prev => ({
      ...prev,
      [field]: value,
    }));
    
    const addressString = [
      newAddress.unit,
      newAddress.building,
      newAddress.street,
      newAddress.subdivision,
      cityOptions.find(option => option.cityId === newAddress.cityId)?.name || '',
      cityOptions.find(option => option.cityId === newAddress.cityId)?.provinceName || '',
      newAddress.zipCode,
    ]
      .filter(Boolean)
      .join(', ');
    updateCoordinates(addressString, true);
  };

  const handleCitySelect = async (event: React.SyntheticEvent, value: any) => {
    if (selectedAddress) {
      if (value) {
        setSelectedAddress((prev: any) => ({
          ...prev!,
          city: { cityId: value.cityId, name: value.name },
          province: { provinceId: value.provinceId, name: value.provinceName },
        }));
        
        const addressString = [
          selectedAddress.unit,
          selectedAddress.building,
          selectedAddress.street,
          selectedAddress.subdivision,
          value.name,
          value.provinceName,
          selectedAddress.zipCode,
        ]
          .filter(Boolean)
          .join(', ');
        updateCoordinates(addressString, false);
      } else {
        setSelectedAddress((prev: any) => ({
          ...prev!,
          city: null,
          province: { provinceId: 0, name: '' },
          coordinatesLatitude: 0,
          coordinatesLongitude: 0,
        }));
      }
    }
  };

  const handleNewCitySelect = async (event: React.SyntheticEvent, value: any) => {
    if (value) {
      setNewAddress(prev => ({
        ...prev,
        cityId: value.cityId,
      }));
      
      const addressString = [
        newAddress.unit,
        newAddress.building,
        newAddress.street,
        newAddress.subdivision,
        value.name,
        value.provinceName,
        newAddress.zipCode,
      ]
        .filter(Boolean)
        .join(', ');
      updateCoordinates(addressString, true);
    } else {
      setNewAddress(prev => ({
        ...prev,
        cityId: 0,
        coordinatesLatitude: 0,
        coordinatesLongitude: 0,
      }));
    }
  };

  const handleNewAddress = () => {
    if (!selectedUser) return;
    setNewAddress({
      userId: selectedUser.userId,
      addresseeFirstName: '',
      addresseeMiddleName: '',
      addresseeLastName: '',
      coordinatesLatitude: 0,
      coordinatesLongitude: 0,
      unit: '',
      building: '',
      street: '',
      subdivision: '',
      cityId: 0,
      zipCode: '',
    });
    setOpenNewAddressDialog(true);
  };

  const handleAccountFlagChange = (flag: keyof AccountFlags, checked: boolean) => {
    setAccountFlags(prev => ({
      ...prev,
      [flag]: checked,
    }));
  };

  
  const handleSaveAccountFlags = async () => {
    if (!selectedUser) return;

    try {
      await dataProvider.setAccountLocked({ userId: selectedUser.userId, locked: accountFlags.isAccountLocked });
      await dataProvider.setEmailVerified({ userId: selectedUser.userId, verified: accountFlags.isEmailVerified });
      await dataProvider.setContractorVerified({ userId: selectedUser.userId, verified: accountFlags.isContractorVerified });
      await dataProvider.setClientPostAllowed({ userId: selectedUser.userId, postAllowed: accountFlags.isClientPostAllowed });
      await dataProvider.setContractorSpecial({ userId: selectedUser.userId, verified: accountFlags.isSpecialContractor });

      if (setSelectedUser) {
        setSelectedUser({
          ...selectedUser,
          isAccountLocked: accountFlags.isAccountLocked,
          isEmailVerified: accountFlags.isEmailVerified,
          isClientPostAllowed: accountFlags.isClientPostAllowed,
          isContractorVerified: accountFlags.isContractorVerified,
          isSpecialContractor: accountFlags.isSpecialContractor,
        });
      }
      if (onAddressSaveSuccess) {
        onAddressSaveSuccess();
      }
      showSuccessMessage('Account flags updated successfully.');
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const renderAddressesTab = () => (
    <Box
      ref={addressesContainerRef}
      sx={{
        display: 'flex',
        height: '500px',
        bgcolor: COLORS_ADMIN_PAGE.BACKGROUND,
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: isResizingAddresses ? 'col-resize' : 'auto',
      }}
    >
      <AddressList
        addresses={addresses}
        selectedAddress={selectedAddress}
        onSelectAddress={setSelectedAddress}
        onNewAddress={handleNewAddress}
        onDeleteAddress={deleteAddress}
        addressesWidth={addressesWidth}
      />

      <ResizeHandle onMouseDown={handleAddressesMouseDown} />

      <Box sx={{
        flex: 1,
        bgcolor: COLORS_ADMIN_PAGE.WHITE,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Address Details
        </Typography>

        <AddressForm
          selectedAddress={selectedAddress}
          cityOptions={cityOptions}
          isGeocodingLoading={isGeocodingLoading}
          onAddressInputChange={handleAddressInputChange}
          onCitySearch={handleCitySearch}
          onCitySelect={handleCitySelect}
          onOpenMapPicker={() => handleOpenMapPicker('existing')}
          onSaveAddress={saveAddress}
          onPrimaryChange={handlePrimaryChange}
        />
      </Box>
    </Box>
  );

  if (!selectedUser) {
    return (
      <Box sx={{ flex: 1, bgcolor: COLORS_ADMIN_PAGE.WHITE, p: 3, overflowY: 'auto' }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          User Profile
        </Typography>
        <Typography>No user selected</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, bgcolor: COLORS_ADMIN_PAGE.WHITE, p: 3, overflowY: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        User Profile
      </Typography>

      <UserBasicInfo
        selectedUser={selectedUser}
        phoneError={phoneError}
        emailError={emailError}
        onInputChange={handleInputChange}
        onPhoneChange={handlePhoneChange}
        onEmailChange={handleEmailChange}
        onBirthdayChange={(newValue:any) => {
          const birthdayString = newValue ? newValue.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
          setSelectedUser({ ...selectedUser, birthday: birthdayString });
        }}
        onUploadAvatarClick={() => {}}
        onOpenChangePasswordModal={handleOpenChangePasswordModal}
        onSaveChanges={handleSaveChanges}
        onFileChange={handleFileChange}
      />

      <Box sx={{ borderBottom: 1, borderColor: COLORS_ADMIN_PAGE.BORDER, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
            '& .Mui-selected': { color: COLORS_ADMIN_PAGE.PRIMARY },
            '& .MuiTabs-indicator': { backgroundColor: COLORS_ADMIN_PAGE.PRIMARY },
          }}
        >
          <Tab label="Addresses" />
          <Tab label="Account Flags" />
        </Tabs>
      </Box>

      {activeTab === 0 && renderAddressesTab()}
      {activeTab === 1 && (
        <AccountFlagsComponent
          accountFlags={accountFlags}
          onFlagChange={handleAccountFlagChange}
          onSave={handleSaveAccountFlags}
        />
      )}

      <NewAddressDialog
        open={openNewAddressDialog}
        newAddress={newAddress}
        cityOptions={cityOptions}
        isGeocodingLoading={isGeocodingLoading}
        onClose={() => {
          setOpenNewAddressDialog(false);
          resetNewAddress();
        }}
        onInputChange={handleNewAddressInputChange}
        onCitySearch={handleCitySearch}
        onCitySelect={handleNewCitySelect}
        onSave={() => {
          saveNewAddress(() => {
            setOpenNewAddressDialog(false);
            if (onAddressSaveSuccess) onAddressSaveSuccess();
          });
        }}
      />

      <MapPicker
        open={openMapPicker}
        onClose={handleCloseMapPicker}
        onLocationSelect={handleMapLocationSelect}
        initialLat={
          currentEditingCoordinates === 'existing' && selectedAddress
            ? selectedAddress.coordinatesLatitude || 14.5995
            : currentEditingCoordinates === 'new'
            ? newAddress.coordinatesLatitude || 14.5995
            : 14.5995
        }
        initialLng={
          currentEditingCoordinates === 'existing' && selectedAddress
            ? selectedAddress.coordinatesLongitude || 120.9842
            : currentEditingCoordinates === 'new'
            ? newAddress.coordinatesLongitude || 120.9842
            : 120.9842
        }
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfile;